import React, { useEffect, useState } from 'react';
import { Typography, Paper, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Button, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Stack, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import DomainIcon from '@mui/icons-material/Domain';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../api/axios';
import PurchasesTable from '../components/PurchasesTable';

interface Tenant {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  email?: string;
}

interface Platform {
  id: number;
  name: string;
  display_name: string;
  is_active: boolean;
}

interface PlatformAssignment {
  platform_id: number;
  platform_name: string;
  credential_id?: number;
  is_active: boolean;
}

interface TenantDomain {
  id: number;
  tenant_id: number;
  domain: string;
  is_active: boolean;
  is_admin_panel: boolean;
  created_at: string;
}

interface TenantsPageProps {
  onTenantClick?: (tenantId: number) => void;
}

const TenantsPage: React.FC<TenantsPageProps> = ({ onTenantClick }) => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [tenantPlatforms, setTenantPlatforms] = useState<{[key: number]: PlatformAssignment[]}>({});
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [domains, setDomains] = useState<string[]>(['']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedTenant, setSelectedTenant] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [platformDialogOpen, setPlatformDialogOpen] = useState(false);
  const [selectedTenantForPlatforms, setSelectedTenantForPlatforms] = useState<number | null>(null);
  const [form, setForm] = useState<Tenant>({ id: 0, name: '', description: '', is_active: true, email: '' });
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
  
  // Domain management state
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);
  const [selectedTenantForDomains, setSelectedTenantForDomains] = useState<number | null>(null);
  const [tenantDomains, setTenantDomains] = useState<TenantDomain[]>([]);
  const [newDomain, setNewDomain] = useState({ domain: '', is_active: true });

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/api/v1/admin/tenants/', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTenants(res.data as Tenant[]);
    } catch (err: any) {
      setError('Failed to fetch tenants');
    }
  };

  const fetchPlatforms = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/api/v1/admin/platforms/all', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlatforms(res.data as Platform[]);
    } catch (err: any) {
      setError('Failed to fetch platforms');
    }
  };

  const fetchTenantPlatforms = async (tenantId: number) => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/api/v1/admin/tenants/${tenantId}/platforms/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTenantPlatforms(prev => ({
        ...prev,
        [tenantId]: res.data as PlatformAssignment[]
      }));
    } catch (err: any) {
      setError('Failed to fetch tenant platforms');
    }
  };

  useEffect(() => {
    fetchTenants();
    fetchPlatforms();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      
      // Create tenant first
      const tenantResponse = await api.post('/api/v1/admin/tenants/', { name, description }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const tenantId = (tenantResponse.data as any).id;
      
      // Add domains if any are provided
      const validDomains = domains.filter(domain => domain.trim() !== '');
      for (const domain of validDomains) {
        try {
          await api.post('/api/v1/admin/tenant-domains', {
            tenant_id: tenantId,
            domain: domain.trim(),
            is_active: true,
            is_admin_panel: false,
          }, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch (domainErr: any) {
          console.warn(`Failed to add domain ${domain}:`, domainErr);
        }
      }
      
      setName('');
      setDescription('');
      setDomains(['']);
      setSuccess('Tenant created successfully');
      fetchTenants();
    } catch (err: any) {
      setError('Failed to create tenant');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setForm({ id: 0, name: '', description: '', is_active: true, email: '' });
  };

  // Domain management functions
  const addDomainField = () => {
    setDomains([...domains, '']);
  };

  const removeDomainField = (index: number) => {
    if (domains.length > 1) {
      setDomains(domains.filter((_, i) => i !== index));
    }
  };

  const updateDomain = (index: number, value: string) => {
    const newDomains = [...domains];
    newDomains[index] = value;
    setDomains(newDomains);
  };

  const handleOpenPlatformDialog = (tenantId: number) => {
    setSelectedTenantForPlatforms(tenantId);
    setPlatformDialogOpen(true);
    fetchTenantPlatforms(tenantId);
  };

  const handleClosePlatformDialog = () => {
    setPlatformDialogOpen(false);
    setSelectedTenantForPlatforms(null);
  };

  const handleAssignPlatform = async (platformId: number, tenantId: number) => {
    try {
      const token = localStorage.getItem('token');
      const platformName = platforms.find(p => p.id === platformId)?.name;
      await api.post(`/api/v1/admin/tenants/${tenantId}/platforms/?platform_name=${platformName}`, {
        credentials: { username: '', password: '' }
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Platform assigned successfully');
      fetchTenantPlatforms(tenantId);
    } catch (err: any) {
      setError('Failed to assign platform');
    }
  };

  const handleUnassignPlatform = async (platformId: number, tenantId: number) => {
    try {
      const token = localStorage.getItem('token');
      await api.post(`/api/v1/admin/platforms/${platformId}/deactivate`, {
        tenant_id: tenantId,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Platform unassigned successfully');
      fetchTenantPlatforms(tenantId);
    } catch (err: any) {
      setError('Failed to unassign platform');
    }
  };

  // Domain management functions
  const handleOpenDomainDialog = (tenantId: number) => {
    setSelectedTenantForDomains(tenantId);
    setDomainDialogOpen(true);
    fetchTenantDomains(tenantId);
  };

  const handleCloseDomainDialog = () => {
    setDomainDialogOpen(false);
    setSelectedTenantForDomains(null);
    setNewDomain({ domain: '', is_active: true });
  };

  const fetchTenantDomains = async (tenantId: number) => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/api/v1/admin/tenants/${tenantId}/domains`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTenantDomains((res.data as TenantDomain[]) || []);
    } catch (err: any) {
      console.error('Failed to fetch tenant domains:', err);
      setTenantDomains([]);
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.domain.trim() || !selectedTenantForDomains) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.post('/api/v1/admin/tenant-domains', {
        tenant_id: selectedTenantForDomains,
        domain: newDomain.domain.trim(),
        is_active: newDomain.is_active,
        is_admin_panel: false,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Domain added successfully');
      fetchTenantDomains(selectedTenantForDomains);
      setNewDomain({ domain: '', is_active: true });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add domain');
    }
  };

  const handleDeleteDomain = async (domainId: number) => {
    if (!window.confirm('Are you sure you want to delete this domain?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/api/v1/admin/tenant-domains/${domainId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Domain deleted successfully');
      if (selectedTenantForDomains) {
        fetchTenantDomains(selectedTenantForDomains);
      }
    } catch (err: any) {
      setError('Failed to delete domain');
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      if (form.id === 0) {
        await api.post('/api/v1/admin/tenants/', { name: form.name, description: form.description }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Tenant created successfully');
      } else {
        await api.put(`/api/v1/admin/tenants/${form.id}/`, { name: form.name, description: form.description }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Tenant updated successfully');
      }
      fetchTenants();
      handleCloseDialog();
    } catch (err: any) {
      setError('Failed to save tenant');
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Tenants</Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <form onSubmit={handleCreate} style={{ marginBottom: 16 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <TextField label="Name" value={name} onChange={e => setName(e.target.value)} required size="small" />
            <TextField label="Description" value={description} onChange={e => setDescription(e.target.value)} size="small" />
            <Button type="submit" variant="contained">Add Tenant</Button>
          </Box>
          
          {/* Domain Configuration */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Domains (Optional)</Typography>
            {domains.map((domain, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                <TextField
                  label={`Domain ${index + 1}`}
                  value={domain}
                  onChange={e => updateDomain(index, e.target.value)}
                  placeholder="https://example.com"
                  size="small"
                  sx={{ flex: 1 }}
                  helperText={index === 0 ? "Include protocol (https://) and full domain" : ""}
                />
                {domains.length > 1 && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeDomainField(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addDomainField}
              variant="outlined"
            >
              Add Domain
            </Button>
          </Box>
        </form>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.filter(tenant => tenant.id !== 1).map((tenant) => (
                <TableRow key={tenant.id}>
                  <TableCell>{tenant.id}</TableCell>
                  <TableCell>
                    {onTenantClick ? (
                      <Button variant="text" onClick={() => onTenantClick(tenant.id)}>{tenant.name}</Button>
                    ) : tenant.name}
                  </TableCell>
                  <TableCell>{tenant.description}</TableCell>
                  <TableCell>{tenant.is_active ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" onClick={() => setSelectedTenant(tenant.id)}>View</Button>
                      <Tooltip title="Manage Platforms">
                        <IconButton size="small" onClick={() => handleOpenPlatformDialog(tenant.id)}>
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Manage Domains">
                        <IconButton size="small" onClick={() => handleOpenDomainDialog(tenant.id)}>
                          <DomainIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {selectedTenant && <PurchasesTable tenantId={selectedTenant} />}
        <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')}><Alert severity="error">{error}</Alert></Snackbar>
        <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')}><Alert severity="success">{success}</Alert></Snackbar>
      </Paper>
      
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>{form.id === 0 ? 'Create Tenant' : 'Edit Tenant'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          />
          <TextField
            label="Email"
            fullWidth
            margin="normal"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={platformDialogOpen} onClose={handleClosePlatformDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Platform Management - {tenants.find(t => t.id === selectedTenantForPlatforms)?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>Available Platforms</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Platform</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {platforms.map((platform) => {
                  const isAssigned = tenantPlatforms[selectedTenantForPlatforms || 0]?.some(
                    tp => tp.platform_id === platform.id && tp.is_active
                  );
                  return (
                    <TableRow key={platform.id}>
                      <TableCell>{platform.display_name || platform.name}</TableCell>
                      <TableCell>
                        {isAssigned ? (
                          <Chip label="Assigned" color="success" size="small" />
                        ) : (
                          <Chip label="Not Assigned" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        {isAssigned ? (
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleUnassignPlatform(platform.id, selectedTenantForPlatforms!)}
                          >
                            Unassign
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleAssignPlatform(platform.id, selectedTenantForPlatforms!)}
                          >
                            Assign
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePlatformDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Domain Management Dialog */}
      <Dialog open={domainDialogOpen} onClose={handleCloseDomainDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Manage Domains for {tenants.find(t => t.id === selectedTenantForDomains)?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Add New Domain Form */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>Add New Domain</Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  label="Domain"
                  value={newDomain.domain}
                  onChange={(e) => setNewDomain({ ...newDomain, domain: e.target.value })}
                  placeholder="https://example.com"
                  sx={{ flex: 1 }}
                  helperText="Include protocol (https://) and full domain"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={newDomain.is_active}
                      onChange={(e) => setNewDomain({ ...newDomain, is_active: e.target.checked })}
                    />
                  }
                  label="Active"
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddDomain}
                  disabled={!newDomain.domain.trim()}
                >
                  Add
                </Button>
              </Box>
            </Paper>

            {/* Existing Domains Table */}
            <Typography variant="h6" gutterBottom>Existing Domains</Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Domain</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenantDomains.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="text.secondary">No domains found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tenantDomains.map((domain) => (
                      <TableRow key={domain.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DomainIcon color="primary" />
                            <Typography variant="body2" fontFamily="monospace">
                              {domain.domain}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={domain.is_active ? 'Active' : 'Inactive'}
                            color={domain.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(domain.created_at).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Delete Domain">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteDomain(domain.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDomainDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantsPage; 